import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  white: '#ffffff',
  textLight: 'rgba(255,255,255,0.85)',
};

const navItems = [
  { name: 'HOME', path: '/' },
  { name: 'PRODUCTS', path: '/products', hasDropdown: true },
  { name: 'APPLICATION', path: '/application' },
  { name: 'ABOUT', path: '/about' },
  { name: 'CAREERS', path: '/careers' },
  { name: 'NEWS', path: '/news' },
  { name: 'CONTACT', path: '/contact' },
];

const productDropdownItems = [
  { name: 'Debit Cards', path: '/productdebit', icon: <CreditCardIcon sx={{ fontSize: 18, mr: 1.5 }} /> },
  { name: 'Credit Cards', path: '/productcredit', icon: <PaymentsIcon sx={{ fontSize: 18, mr: 1.5 }} /> },
];

// Custom styles with Cinzel Bold font
const cinzelBoldStyle = {
  fontFamily: "'Cinzel', serif",
  fontWeight: 700, // Bold weight
  fontStyle: 'normal',
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleMobileAppClick = () => {
    window.open('https://play.google.com/store/apps/details?id=com.diamondcard', '_blank');
  };

  const drawer = (
    <Box sx={{ 
      textAlign: 'center', 
      bgcolor: colors.navy, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Drawer Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: 2.5,
        borderBottom: `1px solid ${colors.gold}33`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            src="/logo/logo.png" 
            alt="Diamond Card Logo"
            sx={{ 
              width: 55, 
              height: 55, 
              mr: 1.5,
              backgroundColor: 'transparent',
              '& img': { objectFit: 'contain' }
            }}
            variant="square"
          />
          <Typography 
            variant="h5" 
            sx={{ 
              color: colors.gold, 
              ...cinzelBoldStyle,
              letterSpacing: 1.5, 
              fontSize: '1.2rem',
            }}
          >
            DIAMOND CARD
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle} sx={{ color: colors.white }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <List sx={{ flex: 1, pt: 2 }}>
        {navItems.map((item) => (
          <ListItem
            key={item.name}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              textAlign: 'center',
              py: 1.8,
              backgroundColor: location.pathname === item.path ? `${colors.gold}1A` : 'transparent',
              borderLeft: location.pathname === item.path ? `3px solid ${colors.gold}` : '3px solid transparent',
              '&:hover': {
                backgroundColor: `${colors.gold}0D`,
              },
            }}
          >
            <ListItemText 
              primary={item.name} 
              sx={{ 
                '& .MuiTypography-root': { 
                  color: location.pathname === item.path ? colors.gold : colors.textLight,
                  fontWeight: location.pathname === item.path ? 700 : 500,
                  letterSpacing: 1.5,
                  fontSize: '0.95rem',
                  fontFamily: "'Cinzel', serif",
                } 
              }} 
            />
          </ListItem>
        ))}
        
        {/* Mobile App Button in Drawer */}
        <ListItem
          onClick={() => {
            handleMobileAppClick();
            handleDrawerToggle();
          }}
          sx={{
            textAlign: 'center',
            py: 1.8,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: `${colors.gold}0D`,
            },
          }}
        >
          <ListItemText 
            primary="MOBILE APP" 
            sx={{ 
              '& .MuiTypography-root': { 
                color: colors.gold,
                fontWeight: 700,
                letterSpacing: 1.5,
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                fontFamily: "'Cinzel', serif",
              } 
            }} 
          />
        </ListItem>
      </List>
      
      <Box sx={{ p: 2.5, borderTop: `1px solid ${colors.gold}33` }}>
        <Typography variant="caption" sx={{ color: colors.textLight, display: 'block', textAlign: 'center', fontSize: '0.7rem', fontFamily: "'Cinzel', serif" }}>
          © {new Date().getFullYear()} Diamond Card
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          bgcolor: colors.navy,
          boxShadow: '0 4px 25px rgba(0,0,0,0.4)',
          borderBottom: `1px solid ${colors.gold}33`,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 0.5, minHeight: { xs: 70, md: 80 } }}>
            {/* Logo and Brand */}
            <Box 
              component={Link} 
              to="/" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.95,
                },
              }}
            >
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card Logo"
                sx={{ 
                  width: { xs: 65, md: 80 }, 
                  height: { xs: 65, md: 80 }, 
                  mr: 2,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' },
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }}
                variant="square"
              />
              <Typography
                variant="h4"
                noWrap
                sx={{
                  ...cinzelBoldStyle,
                  color: colors.gold,
                  letterSpacing: 2,
                  fontSize: { xs: '1.2rem', md: '1.6rem' },
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                DIAMOND CARD
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {navItems.map((item) => (
                  item.hasDropdown ? (
                    <Box key={item.name}>
                      <Button
                        onClick={handleDropdownOpen}
                        sx={{
                          color: location.pathname.includes('/product') ? colors.gold : colors.white,
                          fontWeight: location.pathname.includes('/product') ? 700 : 500,
                          borderBottom: location.pathname.includes('/product') ? `2px solid ${colors.gold}` : '2px solid transparent',
                          borderRadius: 0,
                          px: 2.5,
                          py: 1.2,
                          mx: 0.5,
                          fontSize: '0.9rem',
                          letterSpacing: 1,
                          fontFamily: "'Cinzel', serif",
                          '&:hover': {
                            backgroundColor: `${colors.gold}0D`,
                            color: colors.gold,
                          },
                          transition: 'all 0.3s ease',
                        }}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 20 }} />}
                      >
                        {item.name}
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleDropdownClose}
                        PaperProps={{
                          sx: {
                            bgcolor: colors.navy,
                            backgroundImage: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.darkNavy} 100%)`,
                            border: `1px solid ${colors.gold}33`,
                            borderRadius: 2,
                            minWidth: 240,
                            boxShadow: '0 8px 25px rgba(0,0,0,0.4)',
                          }
                        }}
                        MenuListProps={{
                          sx: {
                            py: 1,
                          }
                        }}
                      >
                        {productDropdownItems.map((dropdownItem) => (
                          <MenuItem
                            key={dropdownItem.name}
                            component={Link}
                            to={dropdownItem.path}
                            onClick={handleDropdownClose}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              py: 1.3,
                              px: 3,
                              color: colors.white,
                              fontFamily: "'Cinzel', serif",
                              '&:hover': {
                                backgroundColor: `${colors.gold}1A`,
                                color: colors.gold,
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <Box sx={{ color: colors.gold, display: 'flex', alignItems: 'center', mr: 1.5 }}>
                              {dropdownItem.icon}
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'inherit', fontFamily: "'Cinzel', serif" }}>
                              {dropdownItem.name}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  ) : (
                    <Button
                      key={item.name}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: location.pathname === item.path ? colors.gold : colors.white,
                        fontWeight: location.pathname === item.path ? 700 : 500,
                        borderBottom: location.pathname === item.path ? `2px solid ${colors.gold}` : '2px solid transparent',
                        borderRadius: 0,
                        px: 2.5,
                        py: 1.2,
                        mx: 0.5,
                        fontSize: '0.9rem',
                        letterSpacing: 1,
                        fontFamily: "'Cinzel', serif",
                        '&:hover': {
                          backgroundColor: `${colors.gold}0D`,
                          color: colors.gold,
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {item.name}
                    </Button>
                  )
                ))}
                
                {/* Mobile App Button */}
                <Tooltip title="Download Mobile App">
                  <Button
                    onClick={handleMobileAppClick}
                    sx={{
                      color: colors.gold,
                      fontWeight: 700,
                      border: `1.5px solid ${colors.gold}`,
                      borderRadius: 50,
                      px: 3,
                      py: 1,
                      ml: 1.5,
                      fontSize: '0.85rem',
                      letterSpacing: 1,
                      fontFamily: "'Cinzel', serif",
                      '&:hover': {
                        backgroundColor: `${colors.gold}1A`,
                        transform: 'translateY(-2px)',
                        borderColor: colors.lightGold,
                      },
                      transition: 'all 0.3s ease',
                    }}
                    startIcon={<PhoneAndroidIcon sx={{ fontSize: 20 }} />}
                  >
                    MOBILE APP
                  </Button>
                </Tooltip>
              </Box>
            ) : (
              <IconButton 
                onClick={handleDrawerToggle} 
                sx={{ 
                  color: colors.gold,
                  '&:hover': { backgroundColor: `${colors.gold}1A` },
                }}
              >
                <MenuIcon sx={{ fontSize: 30 }} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            bgcolor: colors.navy,
            width: 300,
            backgroundImage: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.darkNavy} 100%)`,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;